import {
  Component,
  input,
  output,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export type SoOptionButtonIconType = 'material' | 'svg';

export interface SoOptionButtonConfig {
  id: string;
  title: string;
  description?: string;
  icon?: string;
  iconType?: SoOptionButtonIconType;
  disabled?: boolean;
}

/**
 * Option button component for selectable options with icon, title and description.
 * 
 * Ideal for theme selectors, preference panels, and option lists.
 * 
 * Usage:
 * ```html
 * <so-option-button-ui
 *   [config]="{ id: 'dark', title: 'Oscuro', description: 'Tema dark', icon: 'dark_mode' }"
 *   [selected]="selectedTheme === 'dark'"
 *   (optionSelected)="onThemeSelect($event)" />
 * ```
 */
@Component({
  selector: 'so-option-button-ui',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './so-option-button.component.html',
  styleUrl: './so-option-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SoOptionButtonUI {
  // Required inputs
  config = input.required<SoOptionButtonConfig>();

  // Optional inputs
  selected = input<boolean>(false);
  disabled = input<boolean>(false);
  iconColor = input<string>('--so-secondary');
  iconSize = input<number>(24);
  iconContainerSize = input<number>(44);
  iconBorderRadius = input<number>(12);
  showCheckmark = input<boolean>(true);

  // Outputs
  optionSelected = output<SoOptionButtonConfig>();

  // Computed: effective disabled state
  protected effectiveDisabled = computed(() => this.disabled() || this.config().disabled);

  // Computed: effective icon color (CSS variable or hex)
  protected effectiveIconColor = computed(() => {
    const color = this.iconColor();
    if (color.startsWith('--')) return `var(${color})`;
    if (color.startsWith('#')) return color;
    return `var(--${color})`;
  });

  // Computed: icon background color with opacity
  protected iconBgColor = computed(() => {
    const color = this.iconColor();
    const colorMap: Record<string, string> = {
      '--so-primary': 'rgba(0, 42, 127, 0.15)',
      '--so-secondary': 'rgba(163, 189, 54, 0.15)',
      '--so-danger': 'rgba(244, 67, 54, 0.15)',
      '--so-warning': 'rgba(253, 216, 53, 0.15)',
      '--so-success': 'rgba(100, 221, 23, 0.15)',
      '--so-info': 'rgba(0, 163, 235, 0.15)',
      'so-primary': 'rgba(0, 42, 127, 0.15)',
      'so-secondary': 'rgba(163, 189, 54, 0.15)',
      'so-danger': 'rgba(244, 67, 54, 0.15)',
      'so-warning': 'rgba(253, 216, 53, 0.15)',
      'so-success': 'rgba(100, 221, 23, 0.15)',
      'so-info': 'rgba(0, 163, 235, 0.15)',
    };
    return colorMap[color] || colorMap['--so-secondary'];
  });

  // Event handlers
  onClick(): void {
    if (this.effectiveDisabled()) return;
    this.optionSelected.emit(this.config());
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.onClick();
    }
  }
}
